import { exportDataGrid, type DataGridCell } from 'devextreme/excel_exporter';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import type { DxDataGrid } from 'devextreme-vue/data-grid';
import { useApiErrorSnackbar } from '~/composables/common/useApiErrorSnackbar';
import type { SnackbarMessage, Column, ExportCellFormatters } from '~/types';
import { getExportLimit, getExportUsername } from '~/utils/data-grid.storage';

type DxInstance = NonNullable<InstanceType<typeof DxDataGrid>['instance']>;
type CustomizeExportFn = (options: { gridCell?: DataGridCell; excelCell?: ExcelJS.Cell }) => void;
type ExportFnProp = Function | undefined;

const sanitizeFileName = (name?: string): string | undefined => name?.replace(/\s+/g, '_');

type LoadAllRowsForExport = (options: {
  filter: unknown;
  sort: unknown;
  maxRows: number;
}) => Promise<any[]>;

export interface UseDataGridExportOptions {
  exportFileName?: MaybeRefOrGetter<string | undefined>;
  dataGrid: ComputedRef<DxInstance | undefined>;
  exportCellFormatters?: ExportCellFormatters;
  loadAllRowsForExport?: LoadAllRowsForExport;
}

export const useDataGridExport = ({
  exportFileName,
  dataGrid,
  exportCellFormatters,
  loadAllRowsForExport
}: UseDataGridExportOptions) => {
  const { $i18n } = useNuxtApp();
  const { t } = $i18n;
  const snackbar = useSnackbar();
  const { notifyApiError } = useApiErrorSnackbar();
  const { exportToPdf } = usePdfViewer();
  const { exportTotalCount } = storeToRefs(useStore());

  const exportLimit = getExportLimit();
  const exportLimitPdf = 10000;

  const handleExportPDF = async (fileName: string) => {
    let rows: any[] = [];
    const filter = dataGrid.value?.getCombinedFilter();
    const sort = dataGrid.value?.getDataSource().sort();

    if (loadAllRowsForExport) {
      rows = await loadAllRowsForExport({ filter, sort, maxRows: exportLimitPdf });
    } else {
      const store = dataGrid.value?.getDataSource().store();
      const loadOptions: any = { filter, sort, take: dataGrid.value?.totalCount() || undefined };
      const data: any = await store?.load(loadOptions);

      if (Array.isArray(data)) {
        rows = [...data];
      } else if (data?.data && Array.isArray(data.data)) {
        rows = [...data.data];
      }
    }

    if (rows.length > 0) {
      const columns =
        dataGrid.value
          ?.getVisibleColumns()
          .filter((column: Column) => column.allowExporting && column.name !== 'actions') ?? [];

      await exportToPdf(rows, columns, fileName, 10, exportCellFormatters);
    }
  };

  const handleExportDataFile = async (
    exportType: string,
    fileName: string,
    customizeExportFunction?: CustomizeExportFn
  ) => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = getExportUsername();
    const worksheet = workbook.addWorksheet(sanitizeFileName(toValue(exportFileName)));
    let blobParts: Array<BlobPart>;
    let buffer: ExcelJS.Buffer;
    let downloadFileName: string;

    await exportDataGrid({
      component: dataGrid.value,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: (options) => customizeExportFunction?.(options)
    });

    switch (exportType) {
      case 'XLSX':
        buffer = await workbook.xlsx.writeBuffer();
        blobParts = [buffer];
        downloadFileName = `${fileName}.xlsx`;
        break;
      default:
        buffer = await workbook.csv.writeBuffer();
        blobParts = [new Uint8Array([0xef, 0xbb, 0xbf]), new Uint8Array(buffer)];
        downloadFileName = `${fileName}.csv`;
        break;
    }

    saveAs(
      new Blob(blobParts, { type: 'application/octet-stream;charset=utf-8' }),
      downloadFileName
    );
  };

  const exportData = async (type: string, customizeExportFunction?: ExportFnProp) => {
    try {
      const totalExportedCount = dataGrid.value?.totalCount() || 10;
      const fileName = sanitizeFileName(
        `${toValue(exportFileName)}_${dayjs().toISOString()}`
      ) as string;
      dataGrid.value?.beginCustomLoading('');
      exportTotalCount.value = exportLimit ?? 20000;

      switch (type) {
        case 'XLSX':
        case 'CSV': {
          if (totalExportedCount <= exportLimit || isNaN(exportLimit)) {
            await handleExportDataFile(
              type,
              fileName,
              customizeExportFunction as CustomizeExportFn
            );
          } else {
            snackbar.add({
              type: 'error',
              title: t('common.tooLargeExport', {
                totalCount: totalExportedCount,
                limit: exportLimit
              })
            } as SnackbarMessage);
          }
          break;
        }
        case 'PDF': {
          if (totalExportedCount <= exportLimitPdf) {
            await handleExportPDF(fileName);
          } else {
            snackbar.add({
              type: 'error',
              title: t('common.tooLargeExportPdf', {
                totalCount: totalExportedCount,
                limit: exportLimitPdf
              })
            } as SnackbarMessage);
          }
          break;
        }
      }

      exportTotalCount.value = 0;
      dataGrid.value?.endCustomLoading();
    } catch (error: unknown) {
      const errorResponse = error as { msg: string };
      notifyApiError(error, { title: errorResponse.msg });
    }
  };

  return { exportData };
};
